/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { AuthenticationResult } from "@azure/msal-browser";
import Axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";

import packageJson from "../../../package.json";
import { error as logError } from "../../util/log";

export const defaultHeaders = {
  "Content-Type": "application/json",
};

export type InterceptorPromise = AuthenticationResult | void | null;
export const tokenInterceptor = async (
  config: InternalAxiosRequestConfig,
  fn: () => Promise<InterceptorPromise> | null
) => {
  // App-Version
  const PackageVersion = packageJson.version;
  config.headers["App-Version"] = PackageVersion;

  const token = await fn();
  if (token) {
    config.headers.Authorization = `Bearer ${token.accessToken}`;
  }

  return config;
};

class ApiClient {
  public axiosInstance = Axios.create({
    baseURL: process.env.REACT_APP_BASE_API_URL,
    headers: defaultHeaders,
    timeout: 30000,
  });

  public tokenInterceptorId: number | null = null;

  constructor() {
    this.tokenInterceptorId = null;
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      (err: AxiosError) => {
        logError(err.message, {
          url: err.config?.url,
          method: err.config?.method,
          status: err.response?.status,
          data: err.response?.data,
        });
        return Promise.reject(err);
      }
    );
  }

  public setAxiosInstance(_axiosInstance: AxiosInstance) {
    this.axiosInstance = _axiosInstance;
  }

  public setDefaultToken(token: InterceptorPromise | string) {
    this.axiosInstance.defaults.headers.Authorization = `Bearer ${token}`;
  }

  public setAccountNumber(accountNumber: string) {
    this.axiosInstance.defaults.headers["X-Account-Number"] = accountNumber;
  }

  public async setGetAccessToken(getAccessToken: () => Promise<InterceptorPromise>) {
    if (this.tokenInterceptorId) {
      this.axiosInstance.interceptors.request.eject(this.tokenInterceptorId);
    }
    this.tokenInterceptorId = await this.axiosInstance.interceptors.request.use((config) =>
      tokenInterceptor(config, getAccessToken)
    );
  }

  public isTokenInterceptorEnabled() {
    return !!this.tokenInterceptorId;
  }

  public async get<T, U>(url: string, params?: U): Promise<AxiosResponse<T>> {
    return this.axiosInstance.get<T>(url, { ...params });
  }

  public async post<T, U>(
    url: string,
    data?: U,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.axiosInstance.post<T>(url, data, config);
  }

  public async put<T, U>(url: string, data?: U): Promise<AxiosResponse<T>> {
    return this.axiosInstance.put<T>(url, data);
  }

  public async delete<T>(url: string): Promise<AxiosResponse<T>> {
    return this.axiosInstance.delete<T>(url);
  }
}

export default ApiClient;
